<template>
  <v-card>
    <v-card-title class="text-h5">
      {{ isEditing ? "Editar Webhook" : "Crear Webhook" }}
      <v-spacer></v-spacer>
      <v-btn icon @click="$emit('close')">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-card-title>

    <v-divider></v-divider>

    <v-card-text class="pt-4">
      <v-form ref="form" v-model="valid" autocomplete="off">
        <!-- Paso 1: Seleccionar tipo de webhook -->
        <v-row v-if="!isEditing">
          <v-col cols="12">
            <h3 class="mb-3">Selecciona el tipo de webhook</h3>
            <webhook-type-selector
              v-model="form.type"
              :webhook-types="webhookTypes"
              :disabled="isEditing"
            />
          </v-col>
        </v-row>

        <v-divider v-if="form.type" class="my-4"></v-divider>

        <!-- Configuración básica -->
        <v-row v-if="form.type">
          <v-col cols="12">
            <h3 class="mb-3">Configuración básica</h3>
          </v-col>

          <!-- Nombre -->
          <v-col cols="12" md="6">
            <v-text-field
              v-model="form.name"
              label="Nombre del webhook"
              placeholder="Ej: Enviar leads a Meta"
              :rules="[rules.required]"
              outlined
              dense
            ></v-text-field>
          </v-col>

          <!-- Estado -->
          <v-col cols="12" md="6">
            <v-switch
              v-model="form.enabled"
              label="Webhook activo"
              color="success"
              inset
            ></v-switch>
          </v-col>

          <!-- Selector de entidad y evento -->
          <v-col cols="12">
            <webhook-event-selector
              v-model="form.event"
              :entity-type.sync="form.entity_type"
              :entities="entities"
            />
          </v-col>
        </v-row>

        <v-divider v-if="form.type && typeConfig" class="my-4"></v-divider>

        <!-- Configuración específica del tipo -->
        <v-row v-if="form.type && typeConfig">
          <v-col cols="12">
            <h3 class="mb-3">Configuración de {{ typeConfig.ui.label }}</h3>
            <v-alert
              v-if="typeConfig.ui.description"
              type="info"
              text
              dense
              outlined
              class="mb-4"
            >
              {{ typeConfig.ui.description }}
            </v-alert>
          </v-col>

          <!-- URL del webhook (si lo requiere) -->
          <v-col cols="12" v-if="typeConfig.supports.requires_url">
            <v-text-field
              v-model="form.webhook_url"
              label="URL del webhook"
              placeholder="https://example.com/webhook"
              :rules="[rules.required, rules.url]"
              outlined
              dense
            ></v-text-field>
          </v-col>

          <!-- Método HTTP (si lo soporta) -->
          <v-col
            cols="12"
            md="6"
            v-if="
              typeConfig.supports.http_methods &&
                typeConfig.supports.http_methods.length > 1
            "
          >
            <v-select
              v-model="form.http_method"
              :items="typeConfig.supports.http_methods"
              label="Método HTTP"
              outlined
              dense
            ></v-select>
          </v-col>

          <!-- Campos de contexto dinámicos (si los requiere) -->
          <v-col
            cols="12"
            v-if="
              typeConfig.supports.requires_context && typeConfig.context_fields
            "
          >
            <webhook-context-fields
              v-model="form.context"
              :context-fields="typeConfig.context_fields"
            />
          </v-col>

          <!-- Headers personalizados (si los soporta) -->
          <v-col cols="12" v-if="typeConfig.supports.supports_headers">
            <webhook-headers-config
              v-model="form.headers"
              :default-headers="typeConfig.default_headers"
            />
          </v-col>
        </v-row>

        <v-divider v-if="showConditions" class="my-4"></v-divider>
        <!-- Constructor de condiciones -->
        <v-row v-if="showConditions">
          <v-col cols="12">
            <h3 class="mb-3">Condiciones (opcional)</h3>
            <v-alert type="info" text dense class="mb-4">
              Define condiciones para ejecutar este webhook solo cuando se
              cumplan ciertos criterios.
            </v-alert>
            <webhook-conditions-builder
              v-model="form.conditions"
              :available-fields="availableFields"
              :operators="operators"
              :logic-operators="logicOperators"
            />
          </v-col>
        </v-row>
      </v-form>
    </v-card-text>

    <v-divider></v-divider>

    <v-card-actions class="pa-4">
      <v-spacer></v-spacer>
      <v-btn text @click="$emit('close')">
        Cancelar
      </v-btn>
      <v-btn
        color="primary"
        @click="save"
        :loading="saving"
        :disabled="!form.type"
      >
        {{ isEditing ? "Actualizar" : "Crear" }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import WebhookTypeSelector from "./WebhookTypeSelector.vue";
import WebhookEventSelector from "./WebhookEventSelector.vue";
import WebhookContextFields from "./WebhookContextFields.vue";
import WebhookHeadersConfig from "./WebhookHeadersConfig.vue";
import WebhookConditionsBuilder from "./WebhookConditionsBuilder.vue";

export default {
  name: "WebhookForm",
  components: {
    WebhookTypeSelector,
    WebhookEventSelector,
    WebhookContextFields,
    WebhookHeadersConfig,
    WebhookConditionsBuilder
  },
  props: {
    webhook: {
      type: Object,
      default: null
    },
    metadata: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      valid: false,
      saving: false,
      form: {
        name: "",
        type: null,
        enabled: true,
        entity_type: "lead",
        event: "",
        webhook_url: "",
        http_method: "POST",
        headers: {},
        context: {},
        conditions: []
      },
      rules: {
        required: v => !!v || "Este campo es requerido",
        url: v => {
          if (!v) return true;
          const pattern = /^https?:\/\/.+/;
          return pattern.test(v) || "Debe ser una URL válida";
        }
      }
    };
  },
  computed: {
    isEditing() {
      return !!this.webhook;
    },
    webhookTypes() {
      return this.metadata?.webhook_types || [];
    },
    entities() {
      return this.metadata?.entities || [];
    },
    operators() {
      return this.metadata?.operators || [];
    },
    logicOperators() {
      return this.metadata?.logic_operators || [];
    },
    typeConfig() {
      if (!this.form.type) return null;
      return this.webhookTypes.find(t => t.type === this.form.type);
    },
    availableFields() {
      const availableFields =
        this.entities
          .find(e => e.entity_type === this.form.entity_type)
          ?.events?.find(e => e.event_action === this.form.event)
          ?.available_fields || [];

      return availableFields
        .map(
          field =>
            this.metadata?.fields_conditions[this.form.entity_type][field.key]
        )
        .filter(Boolean);
    },
    showConditions() {
      return (
        this.operators.length &&
        this.form.type &&
        this.typeConfig &&
        this.typeConfig.supports.supports_conditions
      );
    }
  },
  watch: {
    webhook: {
      handler(newValue) {
        this.form = {
          name: newValue.name || "",
          type: newValue.type,
          enabled: newValue.enabled !== undefined ? newValue.enabled : true,
          entity_type: newValue.entity_type || "lead",
          event: newValue.event || "",
          webhook_url: newValue.webhook_url || "",
          http_method: newValue.http_method || "POST",
          headers: newValue.headers || {},
          context: newValue.context || {},
          conditions: newValue.conditions || []
        };
      }
    }
  },
  mounted() {
    if (this.isEditing) {
      this.loadWebhookData();
    }
  },
  methods: {
    loadWebhookData() {
      this.form = {
        name: this.webhook.name || "",
        type: this.webhook.type,
        enabled:
          this.webhook.enabled !== undefined ? this.webhook.enabled : true,
        entity_type: this.webhook.entity_type || "lead",
        event: this.webhook.event || "",
        webhook_url: this.webhook.webhook_url || "",
        http_method: this.webhook.http_method || "POST",
        headers: this.webhook.headers || {},
        context: this.webhook.context || {},
        conditions: this.webhook.conditions || []
      };
    },
    async save() {
      const isValid = this.$refs.form.validate();

      if (!isValid) {
        this.$snotify.error("Por favor, rellena todos los campos", "Error");
        return;
      }

      this.saving = true;
      try {
        const webhookData = { ...this.form };

        // Limpiar campos opcionales vacíos
        if (!webhookData.webhook_url) delete webhookData.webhook_url;
        if (Object.keys(webhookData.headers).length === 0)
          delete webhookData.headers;
        if (Object.keys(webhookData.context).length === 0)
          delete webhookData.context;
        if (webhookData.conditions.length === 0) delete webhookData.conditions;

        if (this.isEditing) {
          await this.$store.dispatch("webhooks/updateWebhook", {
            id: this.webhook._id,
            webhookData
          });
        } else {
          await this.$store.dispatch("webhooks/createWebhook", webhookData);
        }

        this.$emit("save");
      } catch (error) {
        //console.error("Error al guardar webhook:", error);
        this.$snotify.error("Error al guardar webhook", "Error");
        // El error será manejado por el componente padre
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>

<style scoped>
.v-card {
  max-height: 90vh;
  overflow-y: auto;
}
</style>
